import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import { BookDummy } from 'assets';
import BestSellerSvg from 'assets/Icons/Text/BestSellerSvg';
import ComingSoonSVG from 'assets/Icons/Text/ComingSoonSVG';
import FreeSvg from 'assets/Icons/Text/FreeSvg';
import NewSvg from 'assets/Icons/Text/NewSvg';
import TopTenIcon from 'assets/Icons/TopTenIcon';
import clsx from 'clsx';
import Image from 'next/image';
import Link from 'next/link';

import { ShouldRender } from '@/components/shared/basic/ShouldRender';

export interface BooksSliderProps {
  coverImage: string;
  title: string;
  tag?: string;
  topTen?: string;
  slug: string;
  id: string;
  isFree: boolean;
  isExternalLink?: boolean;
  externalLink?: string;
}

interface BookCardProps {
  book: BooksSliderProps;
  notBookLandingPage?: boolean;
}

const BookCardHome = ({ book }: BookCardProps) => {
  const screens = useBreakpoint();

  const getInternalHref = () => {
    if (book.tag === 'COMING SOON') {
      return `/books/${book.slug}-${book.id}/`;
    } else if (book.isFree) {
      return `/books/${book.slug}-${book.id}/?short=true`;
    } else {
      return `/books/${book.slug}-${book.id}/`;
    }
  };

  const href =
    (book.isFree ? book.isExternalLink : !book.isExternalLink) &&
    book.externalLink
      ? book.externalLink
      : getInternalHref();
  const isExternal =
    (book.isFree ? book.isExternalLink : !book.isExternalLink) &&
    !!book.externalLink;

  const tagSvgMap: Record<string, JSX.Element> = {
    free: (
      <FreeSvg
        width={screens.md ? '23' : '17'}
        height={screens.md ? '8' : '6'}
      />
    ),
    new: (
      <NewSvg
        width={screens.md ? '23' : '17'}
        height={screens.md ? '8' : '6'}
      />
    ),
    bestseller: (
      <BestSellerSvg
        width={screens.md ? '55' : '41'}
        height={screens.md ? '8' : '6'}
      />
    ),
    'coming soon': (
      <ComingSoonSVG
        width={screens.md ? '67' : '51'}
        height={screens.md ? '8' : '6'}
      />
    ),
  };

  return (
    <Link
      href={href}
      passHref
      target={isExternal ? '_blank' : undefined}
      rel={isExternal ? 'noopener noreferrer' : undefined}
    >
      <div className='flex justify-center'>
        <div className='relative transition-transform duration-300 ease-in-out group-hover:scale-[1.02]'>
          <Image
            src={book.coverImage || BookDummy}
            alt={book.title}
            width={screens.md ? 168 : 127}
            height={screens.md ? 238 : 180}
            className={clsx(
              'rounded-[4px] transition-transform transform duration-500',
              'max-w-[127px] md:max-w-[168px] h-[180px] md:h-[238px]',
            )}
            style={{ objectFit: 'cover' }}
          />

          <ShouldRender
            check={
              book.tag &&
              !book.topTen &&
              book.tag?.toLowerCase() !== 'bestseller'
            }
          >
            <div
              style={{ boxShadow: '0px 4px 4px 0px #00000040' }}
              className={clsx(
                `absolute top-0 right-0 p-[5px] rounded-bl-[4px] rounded-tr-[4px] font-bold leading-[12px] text-[9px] tracking-[-0.02em] uppercase`,
                {
                  'bg-white ': book.tag?.toLowerCase() === 'bestseller',
                  'bg-[#DC2626] ': book.tag?.toLowerCase() === 'new',
                  'bg-[#280845] ': book.tag?.toLowerCase() === 'coming soon',
                  'bg-primary ': ![
                    'bestseller',
                    'new',
                    'coming soon',
                    'free',
                  ].includes(book.tag?.toLowerCase() || ''),
                },
              )}
            >
              {tagSvgMap[book.tag?.toLowerCase() || ''] ?? book.tag}
            </div>
          </ShouldRender>

          <ShouldRender check={book.isFree}>
            <div
              style={{
                boxShadow: '0px 4px 4px 0px #00000040',
                background: 'linear-gradient(180deg, #282829 0%, #121212 100%)',
              }}
              className='absolute top-0 left-0 p-[5px]   text-[10px] font-bold  leading-[12px] tracking-[-0.02em] text-white rounded-br-[4px] rounded-tl-[4px]'
            >
              {tagSvgMap['free']}
            </div>
          </ShouldRender>

          <ShouldRender check={book.topTen}>
            <div
              style={{
                clipPath: 'polygon(0 0, 100% 0%, 100% 100%, 0 83%)',
                backgroundColor: '#FF5A1F',
                boxShadow: '0px 4px 4px 0px #00000040',
              }}
              className='absolute top-0 right-0 px-1 pt-1 pb-[8px] text-white font-bold text-[10px] rounded-tr-[4px]'
            >
              <div className='flex flex-col items-center leading-tight'>
                <TopTenIcon />
              </div>
            </div>
          </ShouldRender>
        </div>
      </div>
    </Link>
  );
};

export default BookCardHome;
